import { PhraseRootV2Phrase, PhrasesRootData, PhrasesTextElement } from 'services/api/phrase';
import useFormatShortKey from './useFormatShortKey';
import { PhraseValue } from 'hooks/business/usePhraseOnChat';
import { useMemoizedFn } from 'ahooks';

function matchElemsText(
  keyword: string,
  elems: PhraseRootV2Phrase['phraseElems']['elems'],
  allMatch = true,
) {
  let textMatched = false;
  for (let i = 0; i < elems.length; i += 1) {
    const el = elems[i];
    if ((el as PhrasesTextElement).text) {
      const { content, translationContent } = (el as PhrasesTextElement).text;
      textMatched =
        (allMatch || (keyword !== content && keyword !== translationContent)) &&
        (content.toLowerCase().includes(keyword.toLowerCase()) ||
          translationContent.toLowerCase().includes(keyword.toLowerCase()));
      if (textMatched) break;
    }
  }
  return textMatched;
}

const useSearchPhrase = (region?: General.UpperCaseRegion) => {
  const formatShortKey = useFormatShortKey();

  const search = useMemoizedFn(
    (keyword: string, allPhraseRoots?: PhrasesRootData[], allMatch = true) => {
      const lowerKeyword = keyword.toLowerCase();
      return allPhraseRoots?.filter((phraseRoot) => {
        const { groupName, shortcutKey, tags, phraseMap } = phraseRoot;

        // 分组匹配
        const groupNameMatched = groupName.toLowerCase().includes(lowerKeyword);
        // 快捷键匹配
        const shortcutKeyMatched = formatShortKey(shortcutKey.toLowerCase()).includes(lowerKeyword);
        // 标签匹配
        const tagMatched = tags.some((tag) => tag.toLowerCase().includes(lowerKeyword));

        // 快捷短语文本匹配
        let textMatched = false;
        if (region && phraseMap[region]) {
          // 有站点则从站点的配置中匹配
          const elems = phraseMap[region]?.phraseElems.elems || [];
          textMatched = matchElemsText(keyword, elems, allMatch);
        } else {
          const regions = Object.keys(phraseMap);
          for (let i = 0; i < regions.length; i += 1) {
            const region = regions[i];
            textMatched = matchElemsText(
              keyword,
              phraseMap[region]?.phraseElems.elems || [],
              allMatch,
            );
            if (textMatched) break;
          }
        }

        return groupNameMatched || shortcutKeyMatched || tagMatched || textMatched;
      });
    },
  );

  return search;
};

export const useSearchPhraseV2 = () => {
  const formatShortKey = useFormatShortKey();

  const search = (keyword: string, phraseList?: PhraseValue[], allMatch = true) => {
    const lowerKeyword = keyword.toLowerCase();
    return phraseList?.filter((phraseValue) => {
      const { groupName, shortcutKey, tags, phraseElems } = phraseValue;

      // 分组匹配
      const groupNameMatched = groupName.toLowerCase().includes(lowerKeyword);
      // 快捷键匹配
      const shortcutKeyMatched = formatShortKey(shortcutKey.toLowerCase()).includes(lowerKeyword);
      // 标签匹配
      const tagMatched = tags.some((tag) => tag.toLowerCase().includes(lowerKeyword));
      // 快捷短语文本匹配
      const textMatched = matchElemsText(keyword, phraseElems.elems || [], allMatch);

      return groupNameMatched || shortcutKeyMatched || tagMatched || textMatched;
    });
  };

  return search;
};

export default useSearchPhrase;
